import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { LoadingService, RefreshService } from '@firebird-web/shared-services';
import { UserSettingsFacade } from '@firebird-web/user-settings-store';
import { newDataAvailable } from '@firebird-web/ws';
import { Store } from '@ngrx/store';
import { AuthService } from 'libs/acl/src/lib/auth.service';

import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter, map, takeWhile } from 'rxjs';
import { TitleService } from './base/services/title.service';

@Component({
  selector: 'firebird-web-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public title = 'app';
  private _isAlive = true;
  private hiddenAt: number | null = null;
  private readonly SLEEP_THRESHOLD_MS = 5 * 60 * 1000;

  @HostListener('document:visibilitychange')
  public onVisibilityChange() {
    if (document.hidden) {
      this.hiddenAt = Date.now();
      return;
    }
    const hiddenDuration =
      this.hiddenAt != null ? Date.now() - this.hiddenAt : 0;
    this.hiddenAt = null;
    this.store.dispatch(newDataAvailable({ data: null }));
    this.refreshService.tabUnfreezeUpdateTrigger$.next();
    if (hiddenDuration > this.SLEEP_THRESHOLD_MS) {
      this.refreshService.sleepWakeUpdateTrigger$.next();
    }
  }

  @HostListener('document:resume')
  public onResume() {
    this.store.dispatch(newDataAvailable({ data: null }));
    this.refreshService.tabUnfreezeUpdateTrigger$.next();
    this.refreshService.sleepWakeUpdateTrigger$.next();
  }

  constructor(
    private readonly authService: AuthService,
    private readonly store: Store,
    private readonly userService: UserSettingsFacade,
    private readonly refreshService: RefreshService,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly titleService: TitleService
  ) {
    this.loadingService.initNavigationTracking(this.router);
  }

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.authService.login();
    }
    this.userService.init();

    this.handleTitleOnNavigation();
  }

  private handleTitleOnNavigation() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        debounceTime(50),
        map((event: NavigationEnd) => event.urlAfterRedirects),
        takeWhile((_) => this._isAlive)
      )
      .subscribe((urlAfterRedirects: string) => {
        this.titleService.setCurrentTitle(urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this._isAlive = false;
  }
}
